/**
 * GMA-1618 (CompassCare Web 3/14 — Vitals triage) — detail-panel model.
 *
 * Pure descriptors for the VitalsDetailPanel (the drawer body). Per triage
 * vital it resolves: the current value + unit, the "±N% vs baseline" delta, an
 * out-of-range flag (deviation from the personal baseline, same ±10% convention
 * the triage table highlights on), a normal-range band derived from the
 * baseline (for the sparkline), and the 24h trend series. Deviation-vs-baseline
 * is the signal, not raw thresholds — so a value normal for one person and
 * alarming for another reads correctly.
 *
 * UI-free + fail-safe: missing vitals/baselines/trends/device never throw; they
 * resolve to null/empty so the panel degrades gracefully (value "—", no band,
 * no sparkline) rather than crashing on a partial row.
 */
import { deviationPct, formatBaselineDelta, OUT_OF_RANGE_PCT } from "./vitalsModel";
import { classifyVitalReading } from "../../utils/vitalSeverity";

// The four triage vitals shown in the detail panel, with display units. Temp is
// Fahrenheit — the design source shows "98.7°F" and thresholds in °F.
export const PANEL_VITALS = Object.freeze([
  { key: "hr", label: "Heart rate", unit: "bpm" },
  { key: "spo2", label: "SpO₂", unit: "%" },
  { key: "rr", label: "Resp. rate", unit: "br/min" },
  { key: "temp", label: "Temp", unit: "°F" },
]);

const finite = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

// react-app#374 zero-absence contract (shared with VitalsTable): offline rows
// encode absent readings as zero, so a non-positive vital is an absence, not a
// measurement — never a critical-band value.
const reading = (v) =>
  typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;

// Round to 2 decimals so band bounds don't carry float noise (100 * 1.1 =
// 110.000000000000014) into the descriptor / sparkline geometry.
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Per-vital descriptors for a triage row.
 * @param {object} row { vitals, baselines, trends, customThresholds } (any may be missing)
 * @returns {Array<{key,label,unit,value,baseline,delta,deviation,outOfRange,severity,band,series}>}
 */
export function panelVitals(row) {
  const r = row && typeof row === "object" ? row : {};
  const vitals = r.vitals && typeof r.vitals === "object" ? r.vitals : {};
  const baselines = r.baselines && typeof r.baselines === "object" ? r.baselines : {};
  const trends = r.trends && typeof r.trends === "object" ? r.trends : {};
  const customThresholds = r.customThresholds || r.vitalsThresholds || undefined;

  return PANEL_VITALS.map(({ key, label, unit }) => {
    const value = reading(vitals[key]);
    const baseline = finite(baselines[key]);
    const deviation = deviationPct(value, baseline); // null if not computable
    const outOfRange = deviation !== null && Math.abs(deviation) >= OUT_OF_RANGE_PCT;
    const delta = formatBaselineDelta(value, baseline); // null if not computable
    const severityLevel =
      value === null
        ? "unclassified"
        : classifyVitalReading({
            value,
            metricType: key,
            customThresholds,
            deviationPct: deviation,
          });
    // Normal-range band (±OUT_OF_RANGE_PCT of baseline) for the sparkline. A
    // zero/absent baseline gives no meaningful band (and no divide-by-zero).
    const band =
      baseline !== null && baseline !== 0
        ? {
            low: round2(baseline * (1 - OUT_OF_RANGE_PCT / 100)),
            high: round2(baseline * (1 + OUT_OF_RANGE_PCT / 100)),
          }
        : null;
    const series = Array.isArray(trends[key]) ? trends[key] : [];
    return {
      key,
      label,
      unit,
      value,
      baseline,
      delta,
      deviation,
      outOfRange,
      severity: severityLevel,
      band,
      series,
    };
  });
}

/**
 * Device source + battery for the panel footer. Fail-safe: missing/malformed
 * device -> nulls (the panel shows "—").
 * @param {object} row { device: { source, battery } }
 * @returns {{source: string|null, battery: number|null}}
 */
export function panelDevice(row) {
  const d = row && typeof row === "object" && row.device && typeof row.device === "object"
    ? row.device
    : {};
  const source = typeof d.source === "string" && d.source.trim() ? d.source.trim() : null;
  const battery = finite(d.battery);
  return { source, battery };
}
